import React from 'react';
import { motion, type Variants } from 'framer-motion';
import { useIsTouch } from '~/lib/hooks/useMediaQuery';

const IDEAS = [
  {
    label: 'AI Startup',
    icon: 'i-ph:brain',
    prompt:
      'Create a dark theme landing page for an AI analytics startup. Include animated hero with particle effects, feature grid with hover cards, testimonials carousel, and a pricing section with toggle.',
  },
  {
    label: 'SaaS Product',
    icon: 'i-ph:rocket-launch',
    prompt:
      'Design a modern landing page for a project management SaaS. Use a futuristic dark theme with spotlight effects, animated stats, bento grid features, and a compelling CTA with shimmer button.',
  },
  {
    label: 'Fintech App',
    icon: 'i-ph:chart-line-up',
    prompt:
      'Create a premium landing page for a fintech investment app. Luxury dark theme with aurora background, animated number tickers for live stats, trust badges marquee, and a waitlist signup CTA.',
  },
  {
    label: 'Creative Agency',
    icon: 'i-ph:paint-brush-broad',
    prompt:
      'Design a bold landing page for a creative design agency. Use dramatic spotlight effects, wobble cards for portfolio pieces, text reveal animations, and a full-width CTA with gradient background.',
  },
];

const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.5 } },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

/*
 * A real component, always rendered as <ExamplePrompts /> — never called as a
 * function. It uses a hook, and calling it as `ExamplePrompts(fn)` inside
 * BaseChat's render made that hook BaseChat's own: once a prompt was sent
 * (`chatStarted`), the call stopped and BaseChat rendered one hook fewer —
 * React error #300 on every submit (2026-09-15).
 */
export function ExamplePrompts({ onSelect }: { onSelect?: (prompt: string) => void }) {
  // A hover lift sticks after a tap on touch screens.
  const isTouch = useIsTouch();

  return (
    <div id="examples" className="relative mt-4 w-full sm:mt-5">
      <motion.div
        className="flex flex-wrap items-center justify-center gap-2"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Inline label rather than its own sentence — the pills already say
            "pick one", and a second hint line under the composer was noise. */}
        <span className="mr-0.5 hidden font-geist text-[12.5px] text-muted sm:inline">Start from an idea:</span>
        {IDEAS.map((idea, index) => (
          <motion.button
            key={index}
            onClick={() => {
              onSelect?.(idea.prompt);
            }}
            variants={itemVariants}
            whileHover={isTouch ? undefined : { y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="flex flex-shrink-0 items-center gap-2 rounded-full border border-hairline bg-raised px-3.5 py-2 text-[12.5px] font-medium text-secondary transition-colors hover:border-subtle hover:bg-elevated hover:text-primary sm:px-4 sm:py-2.5"
          >
            <span className={`${idea.icon} text-[14px] text-accent`} />
            <span className="whitespace-nowrap">{idea.label}</span>
          </motion.button>
        ))}
      </motion.div>
    </div>
  );
}
